'use client';

import type { UseQueryResult } from '@tanstack/react-query';
import type { ComponentProps, FC } from 'react';
import type { Option } from '../MultiSelect';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from '@/utils/common';
import { MultiSelect } from '../MultiSelect';

type Input = {
  query: { name: string };
  limit?: number;
};

type Output = {
  searchResults: {
    schools: { id: string; name: string }[];
    totalResults: { value: number }[];
  };
};

type UseTRPCQueryHook = (
  input: Input,
  opts?: {
    enabled?: boolean;
    [key: string]: unknown;
  }
) => UseQueryResult<Output, unknown>;

type AsyncMultiSelectProps = {
  useQueryHook?: UseTRPCQueryHook;
  localOptions?: Option[];
  limit?: number;
  debounceMs?: number;
  minSearchLength?: number;
  enableOther?: boolean;
  className?: string;
} & Omit<ComponentProps<typeof MultiSelect>, 'options' | 'isLoading' | 'onSearchChange'>;

export const AsyncMultiSelect: FC<AsyncMultiSelectProps> = ({
  useQueryHook,
  localOptions,
  limit,
  debounceMs = 300,
  minSearchLength = 1,
  enableOther = false,
  searchPlaceholder = 'Type to search...',
  onValueChange,
  className,
  ...multiSelectProps
}) => {
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentSearch, setCurrentSearch] = useState('');

  // Create the debounced function once
  const debouncedSetSearch = useMemo(
    () => debounce((query: string) => setDebouncedSearch(query), debounceMs),
    [debounceMs],
  );

  const handleSearchChange = (query: string) => {
    setCurrentSearch(query);
    debouncedSetSearch(query);
  };

  // Handle local filtering when localOptions are provided
  const filteredLocalOptions = useMemo((): Option[] => {
    if (!localOptions) {
      return [];
    }

    if (!debouncedSearch) {
      return localOptions;
    }

    return localOptions.filter(option =>
      option.label.toLowerCase().includes(debouncedSearch.toLowerCase()),
    );
  }, [localOptions, debouncedSearch]);

  // Only set up the query if we're not using local options
  const queryInput = useMemo((): Input => ({
    query: { name: debouncedSearch },
    limit,
  }), [debouncedSearch, limit]);

  // Determine if the query should be enabled - this controls if the query actually runs
  const isQueryEnabled = Boolean(
    !localOptions
    && useQueryHook
    && debouncedSearch.length >= minSearchLength,
  );

  // Setup default empty values for data, loading and error
  const [data, setData] = useState<Output | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  // Use a dummy hook for when useQueryHook isn't provided
  const dummyHook = useCallback(
    (_input: Input, _opts: { enabled: boolean }) => {
      return { data: undefined, isLoading: false, error: undefined } as UseQueryResult<Output, unknown>;
    },
    [],
  );

  // Always call a hook, but we can use the dummy one if the real one isn't provided
  const hookToUse = useQueryHook || dummyHook;
  const queryResult = hookToUse(queryInput, { enabled: isQueryEnabled });

  // Update our state variables when the query result changes
  useEffect(() => {
    if (isQueryEnabled) {
      setData(queryResult.data);
      setIsLoading(queryResult.isLoading);
      setError(queryResult.error);
    }
  }, [isQueryEnabled, queryResult.data, queryResult.error, queryResult.isLoading]);

  // --- Map the output ---
  const remoteOptions = useMemo((): Option[] => {
    if (localOptions) {
      return [];
    } // Don't process remote options if using local

    // Use optional chaining to safely access nested properties
    const items = data?.searchResults?.schools ?? [];
    return items.map(item => ({
      value: item.id,
      label: item.name,
    }));
  }, [data, localOptions]);

  // Use either local or remote options
  const baseOptions = localOptions ? filteredLocalOptions : remoteOptions;

  // Add "Other" option when enabled and there are no matches
  const options = useMemo((): Option[] => {
    if (!enableOther || !currentSearch.trim()) {
      return baseOptions;
    }

    // Check if current search exactly matches any existing option
    const hasExactMatch = baseOptions.some(option =>
      option.label.toLowerCase() === currentSearch.toLowerCase().trim(),
    );

    // If no exact match and we have a search term, add the "Add [value]" option
    if (!hasExactMatch && baseOptions.length === 0) {
      const addOption: Option = {
        value: currentSearch.trim(),
        label: `Add "${currentSearch.trim()}"`,
      };
      return [addOption];
    }

    return baseOptions;
  }, [enableOther, currentSearch, baseOptions]);

  // Handle value changes and detect when "Add [value]" option is selected
  const handleValueChange = useCallback((values: string[]) => {
    if (onValueChange) {
      onValueChange(values);
    }
  }, [onValueChange]);

  if (error) {
    console.error('Error fetching data for AsyncMultiSelect:', error);
    // Optionally render an error state or fallback
  }

  return (
    <MultiSelect
      options={options}
      isLoading={isLoading && isQueryEnabled}
      onSearchChange={handleSearchChange}
      onValueChange={handleValueChange}
      searchPlaceholder={searchPlaceholder}
      searchable // Force searchable to true for this component
      autoFocusOnSearch
      className={className}
      {...multiSelectProps}
    />
  );
};
